/** The Actions menu's sign-in dialogs (plan "Actions menu" → Sign-in) over the users
 *  endpoints the Authentication tab uses: send a password-reset link (email or SMS — a staff
 *  user gets their dashboard's reset page), or set a password directly (louder; the user's
 *  sessions are revoked). Errors stay in the dialog, the server's password rules listed. */
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as usersApi from '@/api/users';

export function SendResetDialog({
  user,
  onClose,
  onDone,
}: {
  user: Pick<usersApi.ConsoleUserDetail, 'id' | 'email' | 'phone' | 'kind'>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const titleId = useId();
  const [channel, setChannel] = useState<usersApi.ResetChannel>('email');
  const [error, setError] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => usersApi.sendPasswordReset(user.id, channel),
    onSuccess: (r) => onDone(`Reset link sent by ${r.channel === 'sms' ? 'SMS' : 'email'}.`),
    onError: (e: unknown) => setError(e instanceof ConsoleApiError ? e.message : 'Could not send the link.'),
  });
  return (
    <Dialog open onClose={send.isPending ? undefined : onClose} aria-labelledby={titleId} maxWidth="xs" fullWidth>
      <DialogTitle id={titleId}>Send a password reset link</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {user.kind === 'staff' ? 'The link opens their dashboard’s reset page.' : 'The link opens the console’s reset page.'}{' '}
          Confirming the reset revokes their other sessions.
        </Typography>
        <RadioGroup value={channel} onChange={(e) => setChannel(e.target.value as usersApi.ResetChannel)} aria-labelledby={titleId}>
          <FormControlLabel value="email" control={<Radio size="small" />} label={`Email · ${user.email || 'no email'}`} disabled={!user.email} />
          <FormControlLabel value="sms" control={<Radio size="small" />} label={`SMS · ${user.phone || 'no phone'}`} disabled={!user.phone} />
        </RadioGroup>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={send.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={send.isPending || (channel === 'email' ? !user.email : !user.phone)}
          onClick={() => {
            setError(null);
            send.mutate();
          }}
        >
          {send.isPending ? 'Sending…' : 'Send link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function SetPasswordDialog({
  user,
  onClose,
  onDone,
}: {
  user: Pick<usersApi.ConsoleUserDetail, 'id' | 'email'>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const titleId = useId();
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const set = useMutation({
    mutationFn: () => usersApi.setPassword(user.id, password),
    onSuccess: (r) => onDone(`Password set — ${r.tokens_revoked} ${r.tokens_revoked === 1 ? 'session' : 'sessions'} revoked.`),
    onError: (e: unknown) => {
      const body = e instanceof ConsoleApiError ? (e.body as { errors?: unknown }) : null;
      setErrors(
        Array.isArray(body?.errors)
          ? (body.errors as string[])
          : [e instanceof ConsoleApiError ? e.message : 'Could not set the password.'],
      );
    },
  });
  return (
    <Dialog open onClose={set.isPending ? undefined : onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>Set a password for {user.email || `user #${user.id}`}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 1.5 }}>
        <TextField
          type="password"
          label="New password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          size="small"
          sx={{ mt: 1 }}
          autoFocus
        />
        {errors.length > 0 && (
          <Alert severity="error">
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {errors.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary">
          Louder than a reset link: every active session of the user is revoked.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={set.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!password || set.isPending}
          onClick={() => {
            setErrors([]);
            set.mutate();
          }}
        >
          {set.isPending ? 'Setting…' : 'Set password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
